import { PERIODIC_ELEMENTS } from './periodicTableData';

export interface FormulaError {
  index: number;
  message: string;
}

export interface ParsedCompound {
  coefficient: number;
  formula: string;
  state: 'aq' | 'g' | 'l' | 's' | null;
  atoms: Record<string, number>;
}

export interface ParsedEquation {
  arrow: 'equilibrium' | 'forward';
  left: ParsedCompound[];
  right: ParsedCompound[];
}

export interface EquationParseResult {
  equation: ParsedEquation | null;
  error: FormulaError | null;
}

export interface AtomBalance {
  element: string;
  name: string;
  left: number;
  right: number;
  balanced: boolean;
}

export interface BalanceGuidance {
  element: string;
  elementName: string;
  relatedCompounds: string[];
  levelOne: string;
  levelTwo: string;
  levelThree: string;
}

const AUSTRALIAN_ELEMENT_NAMES: Record<string, string> = {
  Al: 'Aluminium',
  Cs: 'Caesium',
};
const ELEMENT_NAMES = new Map(
  PERIODIC_ELEMENTS.map((element) => [
    element.symbol,
    AUSTRALIAN_ELEMENT_NAMES[element.symbol] ?? element.name,
  ]),
);
const FORWARD_ARROWS = ['->', '→'] as const;
const EQUILIBRIUM_ARROWS = ['<=>', '⇌', '⇄'] as const;
const STATE_PATTERN = /\((aq|s|l|g)\)$/i;

function addAtom(target: Record<string, number>, symbol: string, count: number): void {
  target[symbol] = (target[symbol] ?? 0) + count;
}

function mergeAtoms(
  target: Record<string, number>,
  source: Record<string, number>,
  multiplier: number,
): void {
  Object.entries(source).forEach(([symbol, count]) => addAtom(target, symbol, count * multiplier));
}

function parsePositiveInteger(input: string, start: number): { value: number; nextIndex: number } {
  let index = start;
  while (index < input.length && /\d/.test(input[index])) index += 1;
  if (index === start) return { value: 1, nextIndex: start };
  return { value: Number(input.slice(start, index)), nextIndex: index };
}

function parseFormulaAtoms(formula: string): {
  atoms: Record<string, number>;
  error: FormulaError | null;
} {
  const stack: Record<string, number>[] = [{}];
  const openingIndexes: number[] = [];
  let index = 0;

  while (index < formula.length) {
    const character = formula[index];

    if (character === '(' || character === '[') {
      stack.push({});
      openingIndexes.push(index);
      index += 1;
      continue;
    }

    if (character === ')' || character === ']') {
      if (stack.length === 1) {
        return {
          atoms: {},
          error: { index, message: 'This closing bracket has no matching opening bracket.' },
        };
      }
      const expectedOpening = character === ')' ? '(' : '[';
      const openingIndex = openingIndexes.pop() ?? 0;
      if (formula[openingIndex] !== expectedOpening) {
        return {
          atoms: {},
          error: {
            index,
            message: 'Round and square brackets must close in the same order they opened.',
          },
        };
      }
      const group = stack.pop() ?? {};
      const multiplier = parsePositiveInteger(formula, index + 1);
      if (multiplier.value === 0) {
        return {
          atoms: {},
          error: { index: index + 1, message: 'A subscript must be greater than zero.' },
        };
      }
      mergeAtoms(stack[stack.length - 1], group, multiplier.value);
      index = multiplier.nextIndex;
      continue;
    }

    if (/[A-Z]/.test(character)) {
      const next = formula[index + 1];
      const symbol = next && /[a-z]/.test(next) ? `${character}${next}` : character;
      if (!ELEMENT_NAMES.has(symbol)) {
        return {
          atoms: {},
          error: { index, message: `${symbol} is not a recognised element symbol.` },
        };
      }
      const multiplier = parsePositiveInteger(formula, index + symbol.length);
      if (multiplier.value === 0) {
        return {
          atoms: {},
          error: {
            index: index + symbol.length,
            message: 'A subscript must be greater than zero.',
          },
        };
      }
      addAtom(stack[stack.length - 1], symbol, multiplier.value);
      index = multiplier.nextIndex;
      continue;
    }

    if (/\s/.test(character)) {
      index += 1;
      continue;
    }

    return {
      atoms: {},
      error: {
        index,
        message: /[a-z]/.test(character)
          ? 'Element symbols start with a capital letter, for example Na or Cl.'
          : `“${character}” is not valid inside a basic chemical formula.`,
      },
    };
  }

  if (stack.length > 1) {
    return {
      atoms: {},
      error: {
        index: openingIndexes.at(-1) ?? Math.max(0, formula.length - 1),
        message: 'This opening bracket needs a matching closing bracket.',
      },
    };
  }

  if (Object.keys(stack[0]).length === 0) {
    return { atoms: {}, error: { index: 0, message: 'Enter at least one element symbol.' } };
  }

  return { atoms: stack[0], error: null };
}

export function parseCompound(input: string): {
  compound: ParsedCompound | null;
  error: FormulaError | null;
} {
  const trimmed = input.trim();
  const coefficientMatch = trimmed.match(/^(\d+)\s*/);
  const coefficient = coefficientMatch ? Number(coefficientMatch[1]) : 1;
  if (coefficient < 1) {
    return { compound: null, error: { index: 0, message: 'A coefficient must be at least 1.' } };
  }

  let formulaWithState = trimmed.slice(coefficientMatch?.[0].length ?? 0).trim();
  const stateMatch = formulaWithState.match(STATE_PATTERN);
  const state = (stateMatch?.[1]?.toLowerCase() as ParsedCompound['state']) ?? null;
  if (stateMatch) formulaWithState = formulaWithState.slice(0, stateMatch.index).trim();

  const parsed = parseFormulaAtoms(formulaWithState);
  if (parsed.error) {
    return {
      compound: null,
      error: {
        ...parsed.error,
        index: parsed.error.index + (coefficientMatch?.[0].length ?? 0),
      },
    };
  }

  return {
    compound: { coefficient, formula: formulaWithState, state, atoms: parsed.atoms },
    error: null,
  };
}

function findArrow(input: string): {
  arrow: ParsedEquation['arrow'];
  index: number;
  token: string;
} | null {
  const candidates = [
    ...EQUILIBRIUM_ARROWS.map((token) => ({ arrow: 'equilibrium' as const, token })),
    ...FORWARD_ARROWS.map((token) => ({ arrow: 'forward' as const, token })),
  ]
    .map((candidate) => ({ ...candidate, index: input.indexOf(candidate.token) }))
    .filter((candidate) => candidate.index >= 0)
    .sort((left, right) => left.index - right.index || right.token.length - left.token.length);
  return candidates[0] ?? null;
}

function splitSide(side: string): string[] {
  return side
    .split(/\s+\+\s+|\s*\+\s*(?=[A-Z\d(])/)
    .map((compound) => compound.trim())
    .filter(Boolean);
}

function findEmptyCompoundSeparator(side: string): number | null {
  const leading = side.match(/^\s*\+/);
  if (leading) return leading[0].lastIndexOf('+');

  const doubled = side.match(/\+\s*\+/);
  if (doubled?.index !== undefined) return doubled.index;

  const trailing = side.match(/\+\s*$/);
  if (trailing?.index !== undefined) return trailing.index;

  return null;
}

export function parseEquation(input: string): EquationParseResult {
  const trimmed = input.trim();
  if (!trimmed) return { equation: null, error: null };

  const equalsIndex = trimmed.indexOf('=');
  if (equalsIndex >= 0 && !trimmed.includes('<=>')) {
    return {
      equation: null,
      error: {
        index: equalsIndex,
        message:
          'Use a reaction arrow (->), not an equals sign. A chemical equation shows a reaction rather than mathematical equality.',
      },
    };
  }

  const arrow = findArrow(trimmed);
  if (!arrow) {
    return {
      equation: null,
      error: {
        index: trimmed.length,
        message: 'Add an arrow between reactants and products. You can type ->.',
      },
    };
  }

  const leftText = trimmed.slice(0, arrow.index).trim();
  const rightText = trimmed.slice(arrow.index + arrow.token.length).trim();
  if (!leftText || !rightText) {
    return {
      equation: null,
      error: {
        index: arrow.index,
        message: 'The equation needs at least one reactant and one product.',
      },
    };
  }

  const leftInputs = splitSide(leftText);
  const rightInputs = splitSide(rightText);
  const leftEmptySeparator = findEmptyCompoundSeparator(leftText);
  if (leftEmptySeparator !== null) {
    return {
      equation: null,
      error: {
        index: leftEmptySeparator,
        message: 'Add a chemical formula on both sides of each + separator.',
      },
    };
  }
  const rightEmptySeparator = findEmptyCompoundSeparator(rightText);
  if (rightEmptySeparator !== null) {
    return {
      equation: null,
      error: {
        index: arrow.index + arrow.token.length + rightEmptySeparator,
        message: 'Add a chemical formula on both sides of each + separator.',
      },
    };
  }
  const parseSide = (inputs: string[], offset: number) => {
    const compounds: ParsedCompound[] = [];
    let searchFrom = offset;
    for (const item of inputs) {
      const parsed = parseCompound(item);
      if (parsed.error || !parsed.compound) {
        return {
          compounds: [],
          error: {
            index: trimmed.indexOf(item, searchFrom) + (parsed.error?.index ?? 0),
            message: parsed.error?.message ?? 'This chemical formula could not be recognised.',
          },
        };
      }
      compounds.push(parsed.compound);
      searchFrom = trimmed.indexOf(item, searchFrom) + item.length;
    }
    return { compounds, error: null };
  };

  const left = parseSide(leftInputs, 0);
  if (left.error) return { equation: null, error: left.error };
  const right = parseSide(rightInputs, arrow.index + arrow.token.length);
  if (right.error) return { equation: null, error: right.error };

  return {
    equation: {
      arrow: arrow.arrow,
      left: left.compounds,
      right: right.compounds,
    },
    error: null,
  };
}

function sideTotals(compounds: ParsedCompound[]): Record<string, number> {
  const totals: Record<string, number> = {};
  compounds.forEach((compound) => mergeAtoms(totals, compound.atoms, compound.coefficient));
  return totals;
}

export function getAtomBalance(equation: ParsedEquation): AtomBalance[] {
  const left = sideTotals(equation.left);
  const right = sideTotals(equation.right);
  const symbols = [...new Set([...Object.keys(left), ...Object.keys(right)])];
  return symbols.map((element) => ({
    element,
    name: ELEMENT_NAMES.get(element) ?? element,
    left: left[element] ?? 0,
    right: right[element] ?? 0,
    balanced: (left[element] ?? 0) === (right[element] ?? 0),
  }));
}

export function isEquationBalanced(equation: ParsedEquation): boolean {
  const balance = getAtomBalance(equation);
  return balance.length > 0 && balance.every((item) => item.balanced);
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b > 0) {
    const remainder = a % b;
    a = b;
    b = remainder;
  }
  return a;
}

export function getCoefficientScale(equation: ParsedEquation): number {
  const coefficients = [...equation.left, ...equation.right].map(
    (compound) => compound.coefficient,
  );
  return coefficients.reduce(greatestCommonDivisor);
}

export function isSimplestCoefficientRatio(equation: ParsedEquation): boolean {
  return isEquationBalanced(equation) && getCoefficientScale(equation) === 1;
}

export function simplifyCoefficients(equation: ParsedEquation): ParsedEquation {
  const scale = getCoefficientScale(equation);
  if (scale <= 1) return equation;
  return {
    ...equation,
    left: equation.left.map((compound) => ({
      ...compound,
      coefficient: compound.coefficient / scale,
    })),
    right: equation.right.map((compound) => ({
      ...compound,
      coefficient: compound.coefficient / scale,
    })),
  };
}

export function getBalanceGuidance(equation: ParsedEquation): BalanceGuidance | null {
  const imbalance = getAtomBalance(equation).find((item) => !item.balanced);
  if (!imbalance || imbalance.left === 0 || imbalance.right === 0) return null;
  const allCompounds = [...equation.left, ...equation.right];
  const relatedCompounds = allCompounds
    .filter((compound) => compound.atoms[imbalance.element])
    .map((compound) => compound.formula);
  const commonMultiple = Math.max(imbalance.left, imbalance.right);
  const smallerSide = imbalance.left < imbalance.right ? 'reactant' : 'product';

  return {
    element: imbalance.element,
    elementName: imbalance.name,
    relatedCompounds,
    levelOne: `${imbalance.name} is the first element whose atom counts do not match.`,
    levelTwo: `${imbalance.name} appears in ${relatedCompounds.join(' and ')}. A coefficient in front of one of these substances changes every atom inside that substance.`,
    levelThree: `The current counts are ${imbalance.left} and ${imbalance.right}. Look for a shared multiple, starting from ${commonMultiple}, then adjust a ${smallerSide} coefficient without changing a subscript.`,
  };
}

/** Coefficients are whole numbers from 1 to 99 (CT-INTERACT: typed input fails closed). */
export const COEFFICIENT_LIMITS = { min: 1, max: 99 } as const;

/** A specific message for a typed coefficient, or null when it is a whole number from 1 to 99. */
export function coefficientError(raw: string): string | null {
  const text = raw.trim();
  if (text === '') return 'Enter a coefficient from 1 to 99.';
  if (!/^\d+$/.test(text)) return 'A coefficient must be a whole number from 1 to 99.';
  const value = Number(text);
  if (value < COEFFICIENT_LIMITS.min || value > COEFFICIENT_LIMITS.max) {
    return 'A coefficient must be a whole number from 1 to 99.';
  }
  return null;
}

/** Sets one coefficient. Throws on a value outside 1–99 or a non-integer, so the caller keeps the last valid equation. */
export function updateCoefficient(
  equation: ParsedEquation,
  side: 'left' | 'right',
  index: number,
  coefficient: number,
): ParsedEquation {
  if (
    !Number.isInteger(coefficient) ||
    coefficient < COEFFICIENT_LIMITS.min ||
    coefficient > COEFFICIENT_LIMITS.max
  ) {
    throw new Error('A coefficient must be a whole number from 1 to 99.');
  }
  return {
    ...equation,
    [side]: equation[side].map((compound, compoundIndex) =>
      compoundIndex === index ? { ...compound, coefficient } : compound,
    ),
  };
}

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

/** Writes formula subscripts as Unicode subscript digits, for worked text such as "H₂O". */
export function formulaWithSubscripts(formula: string): string {
  return formula.replace(/([A-Za-z)\]])(\d+)/g, (_match, before: string, digits: string) =>
    `${before}${digits.replace(/\d/g, (digit) => SUBSCRIPT_DIGITS[Number(digit)])}`,
  );
}

export interface AtomTallyTerm {
  formula: string;
  coefficient: number;
  atomsPerUnit: number;
  total: number;
  /** For example "2 × H₂O → 2 × 2 = 4 H". */
  working: string;
}

export interface AtomTallyRow extends AtomBalance {
  leftTerms: AtomTallyTerm[];
  rightTerms: AtomTallyTerm[];
  /** For example "4 + 0 = 4", or "0" when the element is missing from the side. */
  leftSum: string;
  rightSum: string;
}

function tallyTerms(compounds: ParsedCompound[], element: string): AtomTallyTerm[] {
  return compounds
    .filter((compound) => compound.atoms[element])
    .map((compound) => {
      const atomsPerUnit = compound.atoms[element];
      const total = compound.coefficient * atomsPerUnit;
      return {
        formula: compound.formula,
        coefficient: compound.coefficient,
        atomsPerUnit,
        total,
        working: `${compound.coefficient} × ${formulaWithSubscripts(compound.formula)} → ${compound.coefficient} × ${atomsPerUnit} = ${total} ${element}`,
      };
    });
}

function sumText(terms: AtomTallyTerm[]): string {
  if (terms.length === 0) return '0';
  const total = terms.reduce((sum, term) => sum + term.total, 0);
  return terms.length === 1 ? String(total) : `${terms.map((term) => term.total).join(' + ')} = ${total}`;
}

/** Per-species atom working for every element: coefficient × atoms per formula unit, then the side sum. */
export function getAtomTally(equation: ParsedEquation): AtomTallyRow[] {
  return getAtomBalance(equation).map((item) => {
    const leftTerms = tallyTerms(equation.left, item.element);
    const rightTerms = tallyTerms(equation.right, item.element);
    return { ...item, leftTerms, rightTerms, leftSum: sumText(leftTerms), rightSum: sumText(rightTerms) };
  });
}

export function equationToInput(equation: ParsedEquation): string {
  const formatSide = (compounds: ParsedCompound[]) =>
    compounds
      .map((compound) => {
        const coefficient = compound.coefficient > 1 ? String(compound.coefficient) : '';
        const state = compound.state ? `(${compound.state})` : '';
        return `${coefficient}${compound.formula}${state}`;
      })
      .join(' + ');
  const arrow = equation.arrow === 'equilibrium' ? '<=>' : '->';
  return `${formatSide(equation.left)} ${arrow} ${formatSide(equation.right)}`;
}

export function getBalanceHint(equation: ParsedEquation): string {
  const imbalance = getAtomBalance(equation).find((item) => !item.balanced);
  if (!imbalance) return 'Every atom count matches. The equation is balanced.';
  if (imbalance.left === 0 || imbalance.right === 0) {
    return `${imbalance.name} appears on only one side. Check that the skeleton equation is complete.`;
  }
  const smallerSide = imbalance.left < imbalance.right ? 'reactant' : 'product';
  return `${imbalance.name} has ${imbalance.left} atom${imbalance.left === 1 ? '' : 's'} on the reactant side and ${imbalance.right} on the product side. Try changing a coefficient on the ${smallerSide} side; do not change the formula subscript.`;
}

export function formulaToSegments(formula: string): Array<{ text: string; subscript: boolean }> {
  const segments: Array<{ text: string; subscript: boolean }> = [];
  formula.match(/[A-Z][a-z]?|\d+|[^A-Za-z\d]+/g)?.forEach((text) => {
    segments.push({ text, subscript: /^\d+$/.test(text) });
  });
  return segments;
}
